"use client";

import { Button } from "@/components/atoms/Button";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn, formatSectionTitle } from "@/lib/utils";
import { RootState } from "@/store/store";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ArrowDown, ArrowUpRight, ChevronDownIcon } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

/** Inline lead form for the right panel (LP layout); self-hydrates from the leaddropform slice. */
const Leaddropform1 = dynamic(
  () => import("@/widgets/leaddropform/LeaddropForm1"),
  { ssr: false },
);

/** Lead form 10 for the enable_lead_form layout; self-hydrates from the leaddropform slice. */
const Leaddropform10 = dynamic(
  () => import("@/widgets/leaddropform/LeaddropForm10"),
  { ssr: false },
);

interface HeroSlide {
  id?: string | number;
  /** Pre-title above the headline */
  pre_title?: string;
  /** Pre-title text color. Empty = reference default white (mobile) / #2A2A2A (desktop) */
  pre_title_color?: string;
  /** Pre-title pill background color. Empty = reference default (brown tint mobile / #FFEFEE desktop) */
  pre_title_bg_color?: string;
  /** Headline line 1 — `{ }` / `{{ }}` markers highlight words (formatSectionTitle) */
  title?: string;
  /** Headline line 2 */
  title2?: string;
  /** Per-slide title base colour (both lines). Empty = default white/black theme */
  title_color?: string;
  /** Per-slide colour for `{ }`-highlighted words + animated text. Empty = yellow/primary */
  title_highlight_color?: string;
  /** Comma-separated words shown in a rotating animation after line 1 */
  animated_text?: string;
  subtitle?: string;
  /** Per-slide subtitle color — DESKTOP only (mobile is fixed #FFFFFF). Empty = #5c5c5c */
  subtitle_color?: string;
  /** Per-slide primary CTA label — empty hides the CTA button for this slide */
  cta_label?: string;
  /** Text shown to the right of the primary CTA (uses the CTA background color) */
  cta_side_text?: string;
  /** Italic offer line under the action row */
  offer_text?: string;
  programs_json?: string;
  /** Per-slide full-bleed background (desktop) — overrides the section background for this slide */
  slide_background?: string;
  /** Per-slide full-bleed background (mobile) — falls back to slide_background, then the section background */
  slide_background_mobile?: string;
  /** Per-slide background alt text */
  slide_background_alt?: string;
  /** Right-panel hero image (sits in the dark side panel) */
  hero_right_image?: string;
  alt_hero?: string;
  /** EMI / cost strip image */
  cost_emi_image?: string;
  cost_emi_image_mobile?: string;
  cost_emi_alt?: string;
  /** Heading shown above the accreditation logos */
  accreditation_heading?: string;
  /** Per-slide accreditation — FILE uploads (order 1 → 4) */
  accreditation_logo_1?: string;
  accreditation_logo_1_alt?: string;
  accreditation_logo_2?: string;
  accreditation_logo_2_alt?: string;
  accreditation_logo_3?: string;
  accreditation_logo_3_alt?: string;
  accreditation_logo_4?: string;
  accreditation_logo_4_alt?: string;
  priority?: number;
  status?: boolean;
}

interface AccLogo {
  image?: string;
  alt_image?: string;
}

interface Banner9Data {
  metadata?: HeroSlide[];
  /** Section-wide background (matches the reference's full-bleed banner image) */
  image?: string;
  mobile_image?: string;
  image_alt?: string;
  slide_frequency?: number;
  hero_section_height?: number;
  hero_container_max_width?: number;
  background_color?: string;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_show?: boolean;
  download_brochure_text?: string;
  download_brochure_link?: string;
  download_brochure_is_cta?: boolean;
  download_brochure_show?: boolean;
  /** Right panel renders the inline lead form instead of the hero image */
  show_lead_form?: boolean;
  /** Lead form 10 replaces the hero image — pinned (doesn't slide with the carousel) on desktop, below the carousel on mobile */
  enable_lead_form?: boolean;
  button_color?: string;
  apply_now_color?: string;
  carousel_color?: string;
  status?: boolean;
}

interface Banner9Props {
  data: Banner9Data | { data: Banner9Data };
}

function resolvePayload(data: Banner9Props["data"]): Banner9Data | undefined {
  const anyData = data as { data?: Banner9Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Banner9Data;
  }
  return data as Banner9Data;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function parsePrograms(
  raw: string | undefined,
): { label: string; href: string }[] {
  if (!raw?.trim()) return [];
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(
        (x): x is { label: string; href: string } =>
          !!x &&
          typeof x === "object" &&
          typeof (x as { label?: string }).label === "string" &&
          typeof (x as { href?: string }).href === "string",
      )
      .map((x) => ({ label: x.label, href: normalizeHref(x.href) }));
  } catch {
    return [];
  }
}

const ACC_LOGO_SLOT_COUNT = 4;

/** Uploaded logos on the slide (order 1 → 4). */
function accLogosFromSlide(slide: HeroSlide): AccLogo[] {
  const logos: AccLogo[] = [];
  for (let i = 1; i <= ACC_LOGO_SLOT_COUNT; i++) {
    const url = slide[`accreditation_logo_${i}` as keyof HeroSlide] as
      | string
      | undefined;
    const alt = slide[`accreditation_logo_${i}_alt` as keyof HeroSlide] as
      | string
      | undefined;
    if (typeof url === "string" && url.trim()) {
      logos.push({
        image: url.trim(),
        alt_image: typeof alt === "string" ? alt.trim() : "",
      });
    }
  }
  return logos;
}

function hasSlideContent(s: HeroSlide): boolean {
  return Boolean(
    s.pre_title?.trim() ||
      s.title?.trim() ||
      s.title2?.trim() ||
      s.subtitle?.trim() ||
      s.hero_right_image?.trim() ||
      s.slide_background?.trim() ||
      s.slide_background_mobile?.trim(),
  );
}

/**
 * Highlight `{ }` / `{{ }}` words in a title. The highlight is ALWAYS the yellow
 * accent (#FFC600) on mobile; on desktop it uses the per-slide highlight colour
 * — delivered via the `--b9-hl` CSS var set on the title element — when one is
 * configured, otherwise the primary theme colour. This keeps the dark-hero
 * mobile treatment fixed while letting the CMS colours drive the desktop layout.
 *
 * Both title fields are textareas, so a hard Enter typed in the CMS is a real
 * line break. `formatSectionTitle` collapses runs of whitespace, so each typed
 * line is highlighted on its own and the lines are rejoined with a `<br/>`.
 */
function buildTitleHtml(title: string, highlightColor?: string): string {
  if (!title) return "";
  const highlightClass = highlightColor?.trim()
    ? "text-yellow-accent min-[1024px]:text-[color:var(--b9-hl)]"
    : "text-yellow-accent min-[1024px]:text-primary";
  return title
    .split(/\r?\n/)
    .map((line) => formatSectionTitle(line, highlightClass))
    .join("<br/>");
}

/** Programme links — match IIT `HomeBanner` link stack. */
const programLinkClass =
  "flex items-center justify-center w-fit min-[1024px]:h-9 text-heading-7 min-[1024px]:text-heading-6 text-white min-[1024px]:text-black my-2 min-[1024px]:px-2.5 min-[1024px]:py-2 rounded-md underline min-[1024px]:no-underline min-[1024px]:border min-[1024px]:border-gray-200 min-[1024px]:bg-white cursor-pointer hover:shadow-md hover:border-transparent whitespace-nowrap";

/**
 * Programme pills above the CTA. ≤5 → plain row; >5 → horizontally scrollable
 * Embla strip with prev/next chevrons (mirrors HomeBanner2's EmblaCarousel).
 */
const ProgramPills = ({
  programs,
}: {
  programs: { label: string; href: string }[];
}) => {
  const scrollable = programs.length > 5;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    dragFree: true,
    containScroll: "trimSnaps",
  });
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (!programs.length) return null;

  if (!scrollable) {
    return (
      <div className="mt-5 w-[300px] self-center min-[768px]:mt-8 min-[768px]:w-[500px] min-[768px]:self-start">
        <div className="flex justify-center gap-2 min-[1024px]:justify-start">
          {programs.map((p) => (
            <Link key={p.href + p.label} href={p.href} className={programLinkClass}>
              {p.label}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 flex w-[300px] items-center gap-3 self-center min-[768px]:mt-8 min-[768px]:w-[496px] min-[768px]:gap-2 min-[768px]:self-start">
      <button
        type="button"
        onClick={scrollPrev}
        aria-label="Previous programmes"
        className="shrink-0 text-white transition min-[1024px]:text-black"
      >
        <ChevronDownIcon className="h-6 w-6 rotate-90" />
      </button>
      <div className="flex-1 overflow-hidden" ref={emblaRef}>
        <div className="flex gap-2">
          {programs.map((p) => (
            <div key={p.href + p.label} className="shrink-0">
              <Link href={p.href} className={programLinkClass}>
                {p.label}
              </Link>
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={scrollNext}
        aria-label="Next programmes"
        className="shrink-0 text-white transition min-[1024px]:text-black"
      >
        <ChevronDownIcon className="h-6 w-6 -rotate-90" />
      </button>
    </div>
  );
};

/**
 * Rotating words appended to the headline (mirrors HomeBanner2 `AnimatedText`).
 * Rendered INSIDE the title's text flow; row height/leading match the title's
 * line-height (36px mobile / 52px desktop) so the word sits on the title's
 * baseline. On desktop (≥1024px) the word is absolutely positioned off a
 * zero-width inline anchor at the end of the title text, so it ALWAYS hangs at
 * the edge of the title's last line — overflowing the 586px text column into
 * the empty banner space — instead of wrapping to its own line below the
 * heading. On mobile it stays in the centered inline flow and wraps like a
 * normal word (hanging would push it off-screen on narrow viewports).
 */
const AnimatedText = ({
  texts,
  color,
}: {
  texts: string[];
  color?: string;
}) => {
  const [current, setCurrent] = useState(0);
  const [step, setStep] = useState(52);

  useEffect(() => {
    const compute = () =>
      setStep(window.innerWidth < 1024 ? 36 : 52);
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    if (texts.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % texts.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [texts.length]);

  if (!texts.length) return null;

  return (
    <span
      className="relative inline-block h-[36px] align-bottom min-[1024px]:h-[52px] min-[1024px]:w-0"
      style={color ? ({ ["--b9-hl"]: color } as React.CSSProperties) : undefined}
    >
      <span className="ml-2 inline-block h-[36px] overflow-hidden text-left min-[1024px]:absolute min-[1024px]:bottom-0 min-[1024px]:left-0 min-[1024px]:h-[52px] min-[1024px]:w-max">
        <span
          className="block transition-transform duration-700 ease-in-out"
          style={{ transform: `translateY(-${current * step}px)` }}
        >
          {texts.map((text, i) => (
            <span
              key={i}
              className={cn(
                "block h-[36px] whitespace-nowrap leading-[36px] min-[1024px]:h-[52px] min-[1024px]:leading-[52px]",
                "text-yellow-accent",
                color
                  ? "min-[1024px]:text-[color:var(--b9-hl)]"
                  : "min-[1024px]:text-primary",
              )}
            >
              {text.trim()}
            </span>
          ))}
        </span>
      </span>
    </span>
  );
};

const Banner9 = (props: Banner9Props) => {
  const sectionData = resolvePayload(props.data);
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);
  const brochureFromCourse = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  const {
    metadata = [],
    image = "",
    mobile_image = "",
    image_alt = "banner-bg",
    slide_frequency = 8000,
    hero_section_height = 564,
    hero_container_max_width = 1114,
    background_color = "rgba(32, 30, 30, 0.8)",
    apply_now_show = true,
    apply_now_text = "Enroll Now",
    apply_now_link = "",
    download_brochure_text = "Download Brochure",
    download_brochure_link = "",
    download_brochure_show = true,
    show_lead_form = false,
    enable_lead_form = false,
    button_color = "#E1473F",
    apply_now_color = "#ffffff",
    carousel_color = "#E1473F",
    status = true,
  } = sectionData || {};

  /** Default on: persisted API wins after migrate. Keys missing → lead popup until toggles explicitly off + URL. */
  const bannerRaw = sectionData as Record<string, unknown> | undefined;
  const applyNowIsCta = isCmsShowFlagOn(
    bannerRaw?.apply_now_is_cta ?? bannerRaw?.applyNowIsCta,
    true,
  );
  const brochureIsCta = isCmsShowFlagOn(
    bannerRaw?.download_brochure_is_cta ?? bannerRaw?.downloadBrochureIsCta,
    true,
  );
  /** Form 10 layout: pinned right panel on desktop + below the carousel on mobile; wins over show_lead_form. */
  const enableLeadForm = isCmsShowFlagOn(enable_lead_form, false);
  const showLeadForm =
    isCmsShowFlagOn(show_lead_form, false) && !enableLeadForm;

  const accentStripColor = background_color || "rgba(32, 30, 30, 0.8)";
  const contentMaxPx = Number(hero_container_max_width) || 1114;
  const rightGutterHalfPx = contentMaxPx / 2;

  /** Desktop room for BOTH headline lines: the text column (which starts at
      the pl-10 gutter) out to the dark band's left edge — 80% of the container
      less the 216px half-hero. Wider than the 586px column, so a headline like
      "India's No. 1 Private University" or "Master of Computer Applications"
      still sits on one line, while a longer one like "Bachelor of Business
      Administration" wraps ("Administration" drops to line 2) instead of
      running under the band. */
  const titleMaxPx = Math.max(contentMaxPx * 0.8 - 216 - 40, 320);

  const slides: HeroSlide[] = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter((s: HeroSlide) => s.status !== false && hasSlideContent(s))
      .sort(
        (a: HeroSlide, b: HeroSlide) => (a.priority ?? 0) - (b.priority ?? 0),
      );
  }, [metadata]);

  const n = slides.length;

  /** Any slide renders the dark right panel — the static right cap then
      completes the band from the hero image's edge to the viewport edge. */
  const anyRightPanel =
    enableLeadForm ||
    showLeadForm ||
    slides.some((s) => Boolean(s.hero_right_image?.trim()));

  useEffect(() => {
    setIndex((i) => (n ? Math.min(i, n - 1) : 0));
  }, [n]);

  const tick = useCallback(() => {
    if (n <= 1) return;
    setIndex((i) => (i + 1) % n);
  }, [n]);

  useEffect(() => {
    if (!status || n <= 1 || !slide_frequency) return undefined;
    const id = window.setInterval(tick, Number(slide_frequency));
    return () => window.clearInterval(id);
  }, [status, n, slide_frequency, tick]);

  const bgDesktopSrc = String(image ?? "").trim();
  const bgMobileSrc = String(mobile_image ?? "").trim();

  const brochureHref = download_brochure_link?.trim()
    ? normalizeHref(download_brochure_link)
    : "";
  const ctaHref = apply_now_link?.trim() ? normalizeHref(apply_now_link) : "";

  /** CTA toggle on, or no URL configured → lead popup */
  const primaryOpensLeadPopup = applyNowIsCta || !ctaHref;
  const brochureOpensLeadPopup = brochureIsCta || !brochureHref;

  const primaryCtaEnabled = isCmsShowFlagOn(apply_now_show);
  const showDownloadBrochure =
    isCmsShowFlagOn(download_brochure_show) &&
    Boolean(download_brochure_text?.trim());

  const handleApplyNowClick = useCallback(
    (label?: string) => {
      ctaClick({
        section: "banner_9",
        component: "hero_apply_now",
        label: label?.trim() || apply_now_text?.trim() || "apply_now",
        step: "1",
        entity: "banner_9",
        parentEntity: "banner_9",
      });
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
    },
    [apply_now_text, dispatch],
  );

  const handleDownloadBrochure = useCallback(() => {
    ctaClick({
      section: "banner_9",
      component: "download_brochure_button",
      label: download_brochure_text,
      step: "1",
      entity: "banner_9",
      parentEntity: "banner_9",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureFromCourse || brochureHref || "",
      }),
    );
  }, [brochureFromCourse, brochureHref, dispatch, download_brochure_text]);

  if (!status || !n) return null;

  const sectionHeight = Number(hero_section_height) || 564;

  return (
    <>
    <section
      className="relative isolate w-full overflow-hidden"
      style={{ height: sectionHeight, fontFamily: "Poppins, sans-serif" }}
    >
      {/* Per-slide full-bleed backgrounds. This track mirrors the content
          track's transform so each slide's background scrolls in with its
          content; a slide with no background falls back to the section-wide
          image / mobile_image (so existing single-background banners are
          unchanged). */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div
          className={cn(
            "flex h-full",
            n > 1 && "transition-transform duration-700 ease-in-out",
          )}
          style={
            n > 1
              ? {
                  width: `${n * 100}%`,
                  transform: `translateX(-${(index * 100) / n}%)`,
                }
              : { width: "100%" }
          }
        >
          {slides.map((slide, slideIdx) => {
            const slideBgRaw = String(slide.slide_background ?? "").trim();
            const slideBgDesktop = slideBgRaw || bgDesktopSrc;
            const slideBgMobile =
              String(slide.slide_background_mobile ?? "").trim() ||
              slideBgRaw ||
              bgMobileSrc;
            const slideBgAlt =
              String(slide.slide_background_alt ?? "").trim() ||
              image_alt ||
              "banner-bg";
            const isFirst = slideIdx === 0;
            const slideHasLogos = accLogosFromSlide(slide).length > 0;
            return (
              <div
                key={`bg-${String(slide.id ?? slideIdx)}`}
                className="relative h-full shrink-0"
                style={n === 1 ? { width: "100%" } : { width: `${100 / n}%` }}
              >
                {slideBgMobile || slideBgDesktop ? (
                  <Image
                    src={slideBgMobile || slideBgDesktop}
                    alt={slideBgAlt}
                    fill
                    priority={isFirst}
                    fetchPriority={isFirst ? "high" : "auto"}
                    sizes="100vw"
                    className="object-cover object-center min-[1024px]:hidden"
                  />
                ) : null}
                {slideBgDesktop || slideBgMobile ? (
                  <Image
                    src={slideBgDesktop || slideBgMobile}
                    alt={slideBgAlt}
                    fill
                    priority={isFirst}
                    fetchPriority={isFirst ? "high" : "auto"}
                    sizes="100vw"
                    className="hidden object-cover object-center min-[1024px]:block"
                  />
                ) : null}
                {/* White gradient fading up from the bottom (mobile only) so the
                    accreditation logos sit on a readable white strip over the
                    photo — only on slides that actually have logos. Kept short
                    and eased so it goes fully white only right behind the logo
                    row at the image's bottom edge instead of washing out the
                    photo. */}
                {slideHasLogos ? (
                  <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-[30%] min-[1024px]:hidden"
                    style={{
                      background:
                        "linear-gradient(to top, #FFFFFF 0%, #FFFFFF 22%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0) 100%)",
                    }}
                    aria-hidden
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Static right band: the gutter beyond the content container plus —
          when slides carry a right panel — the sliver between the hero
          image's right edge (80% + 216px of the container) and the viewport
          edge. The sliding per-slide band ends flush with the hero image; if
          it ran to the slide edge instead, the pixels past the hero would
          sweep across the screen as a dark hairline while the track slides.
          This cap does not move, so it can safely complete the band. Each
          term clamps to 0 separately: with a container narrower than 1080px
          (0.2 × width < 216px) the band is clipped AT the container edge,
          so the cap must start there. */}
      <div
        className="absolute top-0 right-0 z-[1] hidden h-full min-[1024px]:block"
        style={{
          width: anyRightPanel
            ? `calc(max(50% - min(50%, ${rightGutterHalfPx}px), 0px) + max(min(20%, ${contentMaxPx / 5}px) - 216px, 0px))`
            : `calc(50% - ${rightGutterHalfPx}px)`,
          minWidth: 0,
          backgroundColor: accentStripColor,
        }}
        aria-hidden
      />

      <div
        className="mx-auto h-full overflow-hidden"
        style={{ width: "100%", maxWidth: contentMaxPx }}
      >
        <div
          className={cn(
            "flex h-full",
            n > 1 && "transition-transform duration-700 ease-in-out",
          )}
          style={
            n > 1
              ? {
                  width: `${n * 100}%`,
                  transform: `translateX(-${(index * 100) / n}%)`,
                }
              : { width: "100%" }
          }
        >
          {slides.map((slide, slideIdx) => {
            const programs = parsePrograms(slide.programs_json);
            const animatedWords = (slide.animated_text ?? "")
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean);
            const preTitleColor = slide.pre_title_color?.trim();
            const preTitleBg = slide.pre_title_bg_color?.trim();
            const title = slide.title?.trim() ?? "";
            const title2 = slide.title2?.trim() ?? "";
            const titleColor = slide.title_color?.trim();
            const titleHighlight = slide.title_highlight_color?.trim();
            /**
             * Per-slide title colours apply on DESKTOP only (via CSS vars);
             * mobile is fixed to white text + #FFC600 highlight for the dark
             * hero. Empty vars leave the white default on desktop too.
             */
            const titleStyle =
              titleColor || titleHighlight
                ? ({
                    ...(titleColor ? { ["--b9-title"]: titleColor } : {}),
                    ...(titleHighlight ? { ["--b9-hl"]: titleHighlight } : {}),
                  } as React.CSSProperties)
                : undefined;
            const subtitleColor = slide.subtitle_color?.trim();
            const logosForSlide = accLogosFromSlide(slide);
            const ctaLabel = slide.cta_label?.trim() || "";
            const showPrimaryCta = primaryCtaEnabled && Boolean(ctaLabel);
            const ctaSideText = slide.cta_side_text?.trim() ?? "";
            const costEmiDesktop = String(slide.cost_emi_image ?? "").trim();
            const costEmiMobile = String(
              slide.cost_emi_image_mobile ?? "",
            ).trim();
            /** Only the first slide's headline is the page h1; the rest
                demote to h2 so the page keeps a single h1. */
            const TitleTag = slideIdx === 0 ? "h1" : "h2";
            /** enable_lead_form keeps the dark band on EVERY slide so the
                pinned form (rendered outside this sliding track) always has
                its backdrop. */
            const showRightPanel =
              enableLeadForm ||
              showLeadForm ||
              Boolean(slide.hero_right_image?.trim());

            return (
              <div
                key={String(slide.id ?? slideIdx)}
                className={cn(
                  "flex h-full shrink-0 flex-col min-[1024px]:flex-row",
                  n === 1 && "w-full",
                )}
                style={n === 1 ? undefined : { width: `${100 / n}%` }}
              >
                {/* Content column — full slide height on mobile so the inner
                    flex-1 spacer can push the logos to the bottom (onto the
                    white gradient). */}
                <div className="flex h-full w-full min-[1024px]:flex-[3]">
                  <div
                    className={cn(
                      "flex h-full w-full flex-col items-center justify-start gap-5 px-5 pt-24 pb-5",
                      "min-[768px]:w-fit min-[1024px]:w-[586px] min-[1024px]:items-start min-[1024px]:gap-10 min-[1024px]:pt-24 min-[1024px]:pb-10 min-[1024px]:pl-10",
                    )}
                  >
                    <div className="z-10 flex w-full flex-1 flex-col">
                      {slide.pre_title?.trim() ? (
                        <div className="mb-2.5 flex w-full flex-row items-center justify-center min-[1024px]:mb-0 min-[1024px]:justify-start">
                          <p
                            className={cn(
                              "w-fit px-2.5 text-[16px] font-normal leading-normal",
                              // Mobile text is fixed to #FFFFFF; desktop uses the
                              // per-slide colour or #2A2A2A.
                              "text-[#FFFFFF]",
                              preTitleColor
                                ? "min-[1024px]:text-[color:var(--b9-pretitle-color)]"
                                : "min-[1024px]:text-[#2A2A2A]",
                              // Mobile bg is fixed to #8161614D (dark-hero pill);
                              // desktop uses the per-slide bg colour or #FFEFEE.
                              "bg-[#8161614D]",
                              preTitleBg
                                ? "min-[1024px]:bg-[color:var(--b9-pretitle-bg)]"
                                : "min-[1024px]:bg-[#FFEFEE]",
                            )}
                            style={
                              {
                                ...(preTitleColor
                                  ? {
                                      ["--b9-pretitle-color"]: preTitleColor,
                                    }
                                  : {}),
                                ...(preTitleBg
                                  ? { ["--b9-pretitle-bg"]: preTitleBg }
                                  : {}),
                              } as React.CSSProperties
                            }
                          >
                            {slide.pre_title}
                          </p>
                        </div>
                      ) : null}

                      {title || animatedWords.length ? (
                        <TitleTag
                          className={cn(
                            "text-center text-[24px] font-bold leading-[36px] min-[1024px]:text-left min-[1024px]:text-[35px] min-[1024px]:leading-[52px]",
                            /* Same escape hatch as "Title line 2": the heading
                               would otherwise be block-width (546px, the padded
                               586px column) and wrap there — at 35px a headline
                               like "India's No. 1 Private University" is a hair
                               wider, so its last word dropped to a second line.
                               Sizing to max-content first lets `titleMaxPx` —
                               the spare room out to the dark band — decide the
                               wrap point instead. Mobile (24px, centred, ~320px)
                               wraps as before. */
                            "min-[1024px]:w-max min-[1024px]:max-w-[var(--b9-title-max)]",
                            "text-[#FFFFFF]",
                            titleColor &&
                              "min-[1024px]:text-[color:var(--b9-title)]",
                          )}
                          style={
                            {
                              ...titleStyle,
                              ["--b9-title-max"]: `${titleMaxPx}px`,
                            } as React.CSSProperties
                          }
                        >
                          {title ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: buildTitleHtml(title, titleHighlight),
                              }}
                            />
                          ) : null}
                          {animatedWords.length ? (
                            <AnimatedText
                              texts={animatedWords}
                              color={titleHighlight}
                            />
                          ) : null}
                        </TitleTag>
                      ) : null}

                      {title2 ? (
                        <h2
                          className={cn(
                            "text-center text-[24px] font-bold leading-[36px] min-[1024px]:text-left min-[1024px]:text-[35px] min-[1024px]:leading-[52px]",
                            /* "Title line 2" gets more desktop width than the
                               586px content column: at 35px a programme name
                               like "Master of Computer Applications" is ~20px
                               wider than the column and wrapped a single word,
                               and the spare room between the column and the
                               dark band absorbs that overhang. It still wraps
                               once it outgrows that room (`titleMaxPx`) — e.g.
                               "Bachelor of Business Administration" breaks
                               before "Administration" rather than running under
                               the band. Mobile (24px, centred, ~320px) wraps as
                               before. */
                            /* w-max + max-w: the h2 would otherwise be block-
                               width (546px, the padded column) and wrap there
                               regardless of the cap. Sizing to max-content
                               first lets the cap decide the wrap point. */
                            "min-[1024px]:w-max min-[1024px]:max-w-[var(--b9-title-max)]",
                            "text-[#FFFFFF]",
                            titleColor &&
                              "min-[1024px]:text-[color:var(--b9-title)]",
                          )}
                          style={
                            {
                              ...titleStyle,
                              ["--b9-title-max"]: `${titleMaxPx}px`,
                            } as React.CSSProperties
                          }
                          dangerouslySetInnerHTML={{
                            __html: buildTitleHtml(title2, titleHighlight),
                          }}
                        />
                      ) : null}

                      {slide.subtitle ? (
                        <p
                          className={cn(
                            "mt-2.5 text-center text-[16px] font-normal leading-normal min-[1024px]:w-[554px] min-[1024px]:text-left",
                            // Mobile is fixed to pure #FFFFFF for the dark hero
                            // (the theme `white` token is #f8f9fa); desktop uses
                            // the per-slide colour or #5c5c5c.
                            "!text-[#FFFFFF]",
                            subtitleColor
                              ? "min-[1024px]:!text-[color:var(--b9-subtitle)]"
                              : "min-[1024px]:!text-[#5c5c5c]",
                          )}
                          style={
                            subtitleColor
                              ? ({
                                  ["--b9-subtitle"]: subtitleColor,
                                } as React.CSSProperties)
                              : undefined
                          }
                        >
                          {slide.subtitle}
                        </p>
                      ) : null}

                      <ProgramPills programs={programs} />

                      {showPrimaryCta ||
                      showDownloadBrochure ||
                      ctaSideText ||
                      slide.offer_text?.trim() ? (
                        <div
                          className="mt-3.5 flex flex-col items-center justify-center gap-3.5 min-[1024px]:mt-8 min-[1024px]:flex-row min-[1024px]:justify-start"
                          style={
                            {
                              ["--banner-accent" as string]: button_color,
                            } as React.CSSProperties
                          }
                        >
                          {showPrimaryCta ? (
                            primaryOpensLeadPopup ? (
                              <Button
                                type="button"
                                classType="ghost"
                                className="inline-flex w-[186px] flex-row items-center justify-center gap-[10px] rounded-md px-3 py-[6px] !text-heading-6 text-white min-[768px]:gap-2.5 min-[768px]:px-[18px] min-[768px]:py-2"
                                style={{
                                  backgroundColor: button_color,
                                  color: apply_now_color,
                                }}
                                onClick={() => handleApplyNowClick(ctaLabel)}
                              >
                                {ctaLabel}
                                <span className="size-6 shrink-0">
                                  <ArrowUpRight className="size-6 transition-transform duration-300" />
                                </span>
                              </Button>
                            ) : (
                              <Link
                                href={ctaHref}
                                className="inline-flex w-[186px] flex-row items-center justify-center gap-[10px] rounded-md px-3 py-[6px] !text-heading-6 min-[768px]:gap-2.5 min-[768px]:px-[18px] min-[768px]:py-2"
                                style={{
                                  backgroundColor: button_color,
                                  color: apply_now_color,
                                }}
                                onClick={() =>
                                  ctaClick({
                                    section: "banner_9",
                                    component: "hero_apply_now_link",
                                    label: ctaLabel || "apply_now",
                                    step: "1",
                                    entity: "banner_9",
                                    parentEntity: "banner_9",
                                  })
                                }
                              >
                                {ctaLabel}
                                <span className="size-6 shrink-0">
                                  <ArrowUpRight className="size-6 transition-transform duration-300" />
                                </span>
                              </Link>
                            )
                          ) : null}

                          {ctaSideText ? (
                            <p
                              className="text-center text-heading-7 font-semibold min-[1024px]:text-left min-[1024px]:!text-heading-6"
                              style={{ color: button_color }}
                            >
                              {ctaSideText}
                            </p>
                          ) : null}

                          {showDownloadBrochure ? (
                            brochureOpensLeadPopup ? (
                              <Button
                                type="button"
                                classType="ghost"
                                className="flex items-center gap-2 rounded-md border-[1px] border-solid border-white bg-transparent px-[10px] py-2 !text-heading-7 text-white min-[1024px]:border-[color:var(--banner-accent)] min-[1024px]:px-3 min-[1024px]:!text-heading-6 min-[1024px]:leading-5 min-[1024px]:!text-[color:var(--banner-accent)]"
                                onClick={handleDownloadBrochure}
                              >
                                {download_brochure_text}
                                <ArrowDown className="size-5 stroke-white min-[1024px]:size-6 min-[1024px]:stroke-[color:var(--banner-accent)]" />
                              </Button>
                            ) : (
                              <Link
                                href={brochureHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 rounded-md border-[1px] border-white px-[10px] py-2 !text-heading-7 text-white min-[1024px]:border-[color:var(--banner-accent)] min-[1024px]:px-3 min-[1024px]:!text-heading-6 min-[1024px]:leading-5 min-[1024px]:!text-[color:var(--banner-accent)]"
                                onClick={() =>
                                  ctaClick({
                                    section: "banner_9",
                                    component: "download_brochure_link",
                                    label: download_brochure_text,
                                    step: "1",
                                    entity: "banner_9",
                                    parentEntity: "banner_9",
                                  })
                                }
                              >
                                {download_brochure_text}
                                <ArrowDown className="size-5 stroke-white min-[1024px]:size-6 min-[1024px]:stroke-[color:var(--banner-accent)]" />
                              </Link>
                            )
                          ) : null}

                          {slide.offer_text?.trim() ? (
                            <p className="text-body-3 italic text-white min-[1024px]:text-primary">
                              {slide.offer_text}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    {logosForSlide.length > 0 ? (
                      <div className="relative w-full min-[1024px]:mt-4 min-[1024px]:mb-5">
                        <div className="relative z-10 flex w-full flex-col gap-4 max-[1023px]:items-center min-[1024px]:items-start min-[1024px]:gap-2">
                          {slide.accreditation_heading?.trim() ? (
                            <div className="flex w-full min-w-0 items-center gap-3">
                              <div
                                className="h-px min-w-8 flex-1 shrink bg-[rgba(0,0,0,0.2)] min-[1024px]:hidden"
                                aria-hidden
                              />
                              <h2 className="min-w-0 shrink text-center text-heading-6 font-semibold text-black min-[1024px]:text-left">
                                {slide.accreditation_heading}
                              </h2>
                              <div
                                className="h-px min-w-8 flex-1 shrink bg-[rgba(0,0,0,0.2)]"
                                aria-hidden
                              />
                            </div>
                          ) : null}
                          <div className="w-full max-w-full max-[1023px]:flex max-[1023px]:flex-wrap max-[1023px]:justify-center max-[1023px]:gap-x-2 max-[1023px]:gap-y-2.5 min-[1024px]:grid min-[1024px]:grid-cols-6 min-[1024px]:content-start min-[1024px]:justify-items-center min-[1024px]:gap-2">
                            {logosForSlide.map((logo, i) => (
                              <div
                                key={`${String(slide.id ?? slideIdx)}-${i}`}
                                className="relative mx-auto box-border h-[44px] w-[106.33px] min-h-0 min-w-0 shrink-0 overflow-hidden min-[640px]:h-[54px] min-[640px]:w-[117px] min-[1024px]:h-[46px] min-[1024px]:w-[88px]"
                              >
                                {/* px shrinks the content box so the logo draws at
                                    92×44 inside the 106.33×44 tile (mobile only). */}
                                <Image
                                  alt={logo.alt_image || ""}
                                  src={logo.image || ""}
                                  fill
                                  sizes="(max-width: 1023px) 106px, 88px"
                                  className="object-contain object-center px-[7.165px] min-[640px]:px-0"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {costEmiDesktop || costEmiMobile ? (
                      <div className="mt-10 flex self-start">
                        {costEmiDesktop ? (
                          <Image
                            src={costEmiDesktop}
                            alt={slide.cost_emi_alt || ""}
                            width={264}
                            height={100}
                            priority
                            fetchPriority="high"
                            className="hidden min-[1024px]:block"
                          />
                        ) : null}
                        {costEmiMobile ? (
                          <Image
                            src={costEmiMobile}
                            alt={slide.cost_emi_alt || ""}
                            width={184}
                            height={70}
                            priority
                            fetchPriority="high"
                            className="block min-[1024px]:hidden"
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Right panel: dark band with hero image or inline lead form */}
                {showRightPanel ? (
                  <div className="relative hidden h-full min-[1024px]:block min-[1024px]:flex-[2]">
                    {/* Band from the 3:2 seam (80% of the slide) to EXACTLY
                        the hero image's right edge (216px = half the 432px
                        hero). It must NOT reach the slide edge: any band
                        pixels past the hero sweep across the viewport as a
                        dark hairline while the track slides. The static right
                        cap (outside the track) completes the band to the
                        viewport edge instead. */}
                    <div
                      className="absolute inset-y-0 left-1/2 w-[216px]"
                      style={{ backgroundColor: accentStripColor }}
                    >
                      {showLeadForm ? (
                        <div className="absolute top-0 left-0 flex h-full w-[432px] -translate-x-1/2 items-center justify-center overflow-y-auto overflow-x-hidden">
                          <div className="shrink-0 origin-center scale-[0.88]">
                            <Leaddropform1 />
                          </div>
                        </div>
                      ) : !enableLeadForm && slide.hero_right_image ? (
                        <div className="absolute flex h-full w-[432px] -translate-x-1/2 items-center justify-center">
                          <Image
                            src={slide.hero_right_image}
                            alt={slide.alt_hero || "banner"}
                            width={432}
                            height={432}
                            priority={slideIdx === 0}
                            fetchPriority={slideIdx === 0 ? "high" : "auto"}
                            className="h-[432px] w-[432px] object-contain"
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pinned lead form (desktop) — enable_lead_form replaces the hero image
          with lead form 10, rendered ONCE outside the sliding track so it stays
          put while the carousel scrolls behind it. Anchored on the dark band
          edge (80% of the content container) — the exact spot the per-slide
          hero image occupies. */}
      {enableLeadForm ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 mx-auto hidden min-[1024px]:block"
          style={{ maxWidth: contentMaxPx }}
        >
          {/* m-auto (not items/justify-center): safe centering — a card taller
              than the section scrolls from its top edge instead of clipping it.
              The window stays at the 432px hero slot at every desktop width:
              LeaddropForm10 caps itself at 400px from 1430px up, so it always
              fits and stays centred on the band instead of stretching past it.
              w-[360px] gives the card a width below the custom sm (1080px)
              breakpoint where LeaddropForm10 has none of its own. */}
          <div className="pointer-events-auto absolute inset-y-0 left-[80%] flex w-[432px] -translate-x-1/2 overflow-y-auto overflow-x-hidden">
            <div className="m-auto w-[360px] shrink-0 origin-center scale-[0.88] sm:w-auto">
              <Leaddropform10 />
            </div>
          </div>
        </div>
      ) : null}

      {n > 1 ? (
        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 transform gap-3">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className="h-3 w-3 rounded-full transition-all duration-300 ease-in-out"
              style={{
                backgroundColor: i === index ? carousel_color : "#FAE1E0",
              }}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      ) : null}
    </section>

    {/* Mobile: enable_lead_form renders lead form 10 BELOW the carousel in
        normal document flow (the fixed-height section can't host it), matching
        the reference LP layout. */}
    {enableLeadForm ? (
      /* bg-[#FFFFFF]: theme `white` is #f8f9fa — the strip behind the card must
         be pure white. Padding lives on the empty:hidden inner so a page with
         no lead_drop_form configured collapses the strip instead of showing a
         blank padded band. */
      <div className="flex w-full justify-center bg-[#FFFFFF] min-[1024px]:hidden">
        <div className="w-full max-w-[432px] px-4 py-6 empty:hidden">
          <Leaddropform10 />
        </div>
      </div>
    ) : null}
    </>
  );
};

export default Banner9;
